import { Component, OnInit, HostListener, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { AF_CONSTANTS, AfPcDomain } from '../model/af.model';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ProgressBarEvent, ChangeNavigationEvent, ValidateAndNavigateEvent, BeforeSubmitErrorEvent, ShowProgressEvent, ShowErrorEvent, SkipToMainContentEvent } from '../service/events';
import { EventService } from '../service/event.service';
import { Router } from '@angular/router';
import { AuthService } from '../service/auth.service';
import { DatePipe } from '@angular/common';
import { CustomValidatorsService } from '../service/custom-validators.service';
import { BaseComponent } from '../base-component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PoliticalContributionService } from '../service/political-contribution.service';

@Component({
    selector: 'app-pc-gbam',
    templateUrl: './pc-gbam.component.html',
    styleUrls: ['./pc-gbam.component.scss'],
    standalone: false
})
export class PCGBAMComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  gbamSurvey: AfPcDomain;
  contributionData: UntypedFormGroup;
  signatureImg: string; // @Input wet-signature
  sigCheckValue: boolean = false;
  contributionListChange: boolean = false;
  date: any;
  check: boolean = false;
  blueBorder: boolean = false;
  redBorder: boolean = false;
  checkRadio: boolean = false;

  @ViewChild("addElement") addElement: ElementRef;
  @ViewChild("addButtonElement") addButtonElement: ElementRef;

  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;

  //Form Array
  get pcGbamSignature() { return this.contributionData.get('pcGbamSignature') };
  get pcGbamList() { return this.contributionData.get('pcGbamList') as UntypedFormArray; };
  get bookmarkUrl() { return this.contributionData.get('bookmarkUrl') };
  get pcGbamAccepted() { return this.contributionData.get('pcGbamAccepted') };
  get pcGbamSignedName() { return this.contributionData.get('pcGbamSignedName') };
  get submit() { return this.contributionData.get('submit') };
  get verifyId() { return this.contributionData.get('verifyId') };
  get contribution() { return this.contributionData.get('contribution') };

  constructor(private eventBus: EventService, private _router: Router, private fb: UntypedFormBuilder, private userService: AuthService,
    private datepipe: DatePipe, private customValidator: CustomValidatorsService, private _snackBar: MatSnackBar, private politicalService: PoliticalContributionService) {
    super(eventBus, _router);
  }

  ngOnInit() {
    this.createGroup();
    this.subscribeEvents();
  }

  ngAfterViewInit() {
    this.contributionData.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.contributionData);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    this.pcGbamAccepted.valueChanges.subscribe(() => {
      this.setDate();
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/pc-gbam");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));

    this.handleZoom();
  }

  subscribeEvents() {
    this.politicalService.getPcGBAM().subscribe({
      next: (data: AfPcDomain) => {
        this.gbamSurvey = data;
        this.contributionData.patchValue(data);
        this.signatureImg = data.pcGbamSignature;
        this.date = data.pcGbamWetSignatureDate;
        this.verifyId.setValue(this.userService.getVerifyId());

        for (let contribution of this.gbamSurvey.pcGbamList) {
          let fg: UntypedFormGroup = this.fieldsGroup();
          fg.patchValue(contribution);
          this.pcGbamList.push(fg);
        }
        this.blueBorder = false;
      }
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/pc-gbam') {
          this.submit.setValue(false);
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitGbamSurvey((this.contributionData.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitGbamSurvey((this.contributionData.value), event);
          } else {
            let hasContribution: boolean = this.hasContribution();
            if (hasContribution) {
              if (this.pcGbamList.length > 0) {
                this.pcGbamList.markAllAsTouched();
              } else {
                this.createContribution('');
                this.focusTarget();
              }
              this.eventBus.emit(new ShowProgressEvent({ show: false }));
              this.eventBus.emit(new ShowErrorEvent({ httpStatus: 500, text: 'At least one Contribution is Required.' }));
            } else if (this.validate()) {
              this.submitGbamSurvey((this.contributionData.value), event);
            } else {
              this.eventBus.emit(new BeforeSubmitErrorEvent({}));
            }
          }
        }
      }
    );

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/pc-gbam") {
          let mainContentElement = document.querySelector("#main-content");//check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true);
          }
        }
      }
    );

    this.eventBus.emit(new ProgressBarEvent({ id: 0 }));
  }

  hasContribution(): boolean {
    let indicatedContribution: boolean = (this.contribution.value == 1);

    let contributionList: boolean = false;
    if (this.pcGbamList.length > 0 && this.pcGbamList.valid) {
      contributionList = true;
    }

    return (indicatedContribution && !contributionList);
  }

  focusTarget() {
    setTimeout(() => {
      this.addElement.nativeElement.focus();
      this.redBorder = true;
    });
  }

  onFocusOutMainContent(event) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  submitGbamSurvey(contributionData: AfPcDomain, event) {
    contributionData.pcGbamWetSignatureDate = this.date;
    contributionData.pcGbamSignature = this.signatureImg;

    this.politicalService.savePcGBAM(contributionData).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  createGroup() {
    this.contributionData = this.fb.group({
      verifyId: [],
      contribution: ['', Validators.required],
      pcGbamSignature: ['', Validators.required],
      pcGbamSignedName: ['', [Validators.required, Validators.maxLength(250), this.customValidator.validateRequiredField, Validators.pattern(this.regexName)]],
      pcGbamWetSignatureDate: [''],
      pcGroup: ['', [Validators.required, Validators.maxLength(250), this.customValidator.validateRequiredField]],
      pcGbamList: new UntypedFormArray([]),
      pcGbamAccepted: ['', Validators.requiredTrue],
      bookmarkUrl: [''],
      submit: [false],
    });
  }

  createContribution(check: string) {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.pcGbamList.push(fg);
    setTimeout(() => {
      if (check == 'click') {
        this.addElement.nativeElement.focus();
      }
      this.blueBorder = true;
    }, 0);
  }

  deleteClick(i) {
    this.pcGbamList.removeAt(i);

    setTimeout(() => {
      if (this.pcGbamList && this.pcGbamList.length == 0) {
        this.addButtonElement.nativeElement.focus();
        this.blueBorder = false;
      } else {
        this.addElement.nativeElement.focus();
        this.blueBorder = true;
      }
    }, 0);
  }

  fieldsGroup(): UntypedFormGroup {
    return this.fb.group({
      contributionDate: ['', [Validators.required, Validators.maxLength(100)]],
      recipientName: ['', [Validators.required, this.customValidator.validateRequiredField]],
      officeRunning: ['', [Validators.required, this.customValidator.validateRequiredField]],
      amount: ['', [Validators.required, this.customValidator.validateRequiredField]],
      address: ['', [Validators.required, this.customValidator.validateRequiredField]]
    });
  }

  getPercentage(container: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;

    if (this.contribution.value == 0) {
      return 100;
    }

    for (let controlKey in container.controls) {
      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }

    for (let container of this.contributionData.get('pcGbamList')['controls']) {
      for (let controlKey in container.controls) {
        if (container.controls.hasOwnProperty(controlKey)) {
          let control = container.controls[controlKey];
          if (control.validator != null) {
            totalCount += container.controls[controlKey].validator.length;
            if (control.errors) {
              errorCount += Object.keys(control.errors).length;
            }
          }
        }
      }
    }

    let percentage = ((totalCount - errorCount) / totalCount) * 100;
    return percentage;
  }

  getUrl(sig: string) {
    this.signatureImg = sig; // Gets the base64 String from wet-signature @Output
    this.pcGbamSignature.setValue(sig);
  }

  sigCheck(sigCheck: boolean) {
    this.sigCheckValue = sigCheck;
  }

  setDate() {
    if (this.pcGbamAccepted.value) {
      if (!this.date) {
        if (!this.gbamSurvey.pcGbamWetSignatureDate) {
          this.date = this.datepipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');
        }
      }
    } else if (!this.gbamSurvey.pcGbamWetSignatureDate) {
      this.date = null;
    }
  }

  validate() {
    if (this.contribution.value == 0) {
      return true;
    }

    if (!this.pcGbamAccepted.value) {
      this.pcGbamAccepted.setErrors({ invalid: true });
      this.check = true;
    }

    if (!this.pcGbamSignature.value) {
      this.pcGbamSignature.setErrors({ invalid: true });
    }

    this.submit.setValue(true);
    this.contributionData.markAllAsTouched();
    return !this.contributionData.invalid;
  }


  confirmOnBlur() {
    if (this.pcGbamAccepted.value) {
      this.check = false;
    } else {
      this.check = true;
    }
  }

  confirmOnFocus() {
    this.check = false;
  }

  radioOnBlur() {
    if (this.contribution.value) {
      this.checkRadio = false;
    } else if (this.contribution.value === 0) {
      this.checkRadio = false;
    } else {
      this.checkRadio = true;
    }
  }

  radioOnFocus() {
    this.checkRadio = false;
  }

  @HostListener('window:resize', ['$event'])
  @HostListener('window:load', ['$event'])
  handleZoom() {
    const windowWidth = window.innerWidth;
    if (windowWidth <= AF_CONSTANTS.windowWidth) {
      this.contributionListChange = true;
    } else {
      this.contributionListChange = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  clearBorder(keyDown: KeyboardEvent) {
    if (keyDown.key === 'Tab') {
      this.blueBorder = false;
      this.redBorder = false;
    }
  }

  @HostListener('click', ['$event'])
  clearBorderClick() {
    this.blueBorder = false;
    this.redBorder = false;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
